import { useEffect, useState } from 'react';
import {
    AccountLayout,
    createCloseAccountInstruction,
    getAssociatedTokenAddress,
    getAssociatedTokenAddressSync,
    NATIVE_MINT,
    TOKEN_2022_PROGRAM_ID,
    unpackAccount,
    unpackMint,
} from '@solana/spl-token';
import {
    AccountInfo,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useSplToken } from '../../hooks/useSplToken';
import { useJito } from '../../hooks/useJito';

import getSleepTime from '../../utils/get-sleep-time';
import sleep from '../../utils/sleep';
import '../../LiquidityV3.css';
import { useUrlParams } from '../../hooks/useUrlParams';
import { useSessionGateway } from '../../hooks/useSessionGateway';
import { use0Slot } from '../../hooks/use0Slot';
import {
    removeLiquidityPumpswapBurnLpPriorityGas,
    removeLiquidityPumpswapJitoTip,
    removeLiquidityPumpswapManualRemovePriorityGas,
    removeLiquidityPumpswapRemovePriorityGas,
    removeLiquidityPumpswapRemoveUnits,
} from '../../../ConfigFees';
import { usePumpswap } from '../../hooks/usePumpswap';
import { Currency, CurrencyAmount, SPL_ACCOUNT_LAYOUT, WSOL } from '@raydium-io/raydium-sdk';
import { bigIntToStringWithDecimals } from '../../utils/bigInt-to-string-with-decimals';
import { pumpAmmProtocolFees } from '../../utils/constants';
import { removeLiquidityPumspwapGoalReachedTicksToRemove } from '../../../ConfigConstants';

const SCANNER_CHUNK_SIZE = 100;

export function RemoveLiquidityPumpswap({
    wallet,
    mainConnection,
    connections,
    walletLoaded,
    totalWalletsBalance,
    logMessage,
    deployedToken,
    updatedAt,
    autoAction,
    allWallets,
    currentSlot,
    volFunder,
}: {
    wallet: Keypair;
    mainConnection: Connection;
    connections: Connection[];
    walletLoaded: boolean;
    totalWalletsBalance: number;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    deployedToken: string;
    updatedAt: string;
    autoAction: string;
    allWallets: Keypair[];
    currentSlot: number;
    volFunder: Keypair | undefined;
}) {
    //const { getLpMint, withdraw, getPumpSwapPool } = usePumpswap();
    const { burnToken } = useSplToken();
    const urlParams = useUrlParams();
    const { getPoolId, getLpMint, buildRemoveLiquidityTx } = usePumpswap();
    const { send0SlotTransaction } = use0Slot();

    const [isJitoTx, setIsJitoTx] = useState<boolean>(true);
    const [jitoTip, setJitoTip] = useState<number>(removeLiquidityPumpswapJitoTip / LAMPORTS_PER_SOL);

    const [removeLpAmount, setRemoveLpAmount] = useState<string>('-');
    const [repeatsNumber, setRepeatsNumber] = useState<number>(1);
    const [burnConfirmation, setBurnConfirmation] = useState<boolean>(false);

    const [burnLpAmount, setBurnLpAmount] = useState<number>(0);

    const [tokensPercentageUi, setTokensPercentageUi] = useState<string>('0');
    const [tokensValueUi, setTokensValueUi] = useState<string>('0');

    const { scannerConnectionIndex } = urlParams;
    const [scannerConnection, setScannerConnection] = useState(connections[0]);

    const [scanningEnabled, setScanningEnabled] = useState<boolean>(false);

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);
    const [poolBalance, setPoolBalance] = useState<string>('0');

    const [autoRemoveGoalSol, setAutoRemoveGoalSol] = useState<number>(0.2);
    const [autoRemoveGoalSolEnabled, setAutoRemoveGoalSolEnabled] = useState<boolean>(true);

    const [profitReachedSlots, setProfitReachedSlots] = useState<number>(0);
    const [lastProfitReachedSlot, setLastProfitReachedSlot] = useState<number>(0);

    useEffect(() => {
        if (autoRemoveGoalSolEnabled) {
            if (autoRemoveGoalSol * LAMPORTS_PER_SOL > Number(tokensValueUi) * LAMPORTS_PER_SOL) {
                console.debug(`Auto remove goal not reached: ${autoRemoveGoalSol * LAMPORTS_PER_SOL} < ${Number(tokensValueUi)}`);
                setProfitReachedSlots(0);
                return;
            }
            if (currentSlot === lastProfitReachedSlot) {
                console.debug(`Current slot is the same as last profit reached slot: ${currentSlot}`);
                return;
            }
            logMessage(`Auto removeP: Goal reached. Current number of slots: ${profitReachedSlots + 1}`, 'success');
            // logMessage(`Auto remove goal reached`, 'success');
            if (profitReachedSlots + 1 >= removeLiquidityPumspwapGoalReachedTicksToRemove) {
                logMessage(`Profit found for ${removeLiquidityPumspwapGoalReachedTicksToRemove} tick(s) already, removing liquidity`);
                removeLiquidity(Number(removeLpAmount).toString());
                setAutoRemoveGoalSolEnabled(false);
            }
            setProfitReachedSlots((prev) => prev + 1);
            setLastProfitReachedSlot(currentSlot);
        }
    }, [profitReachedSlots, currentSlot, tokensValueUi, autoRemoveGoalSolEnabled, autoRemoveGoalSol, removeLpAmount, lastProfitReachedSlot]);

    useEffect(() => {
        setScannerConnection(connections[scannerConnectionIndex] || connections[0]);
    }, [scannerConnectionIndex]);

    useEffect(() => {
        if (!deployedToken) return;
        try {
            getLpBalance();
        } catch (e) {
            console.log(e);
        }
    }, [deployedToken]);

    useEffect(() => {
        if (!scanningEnabled) return;
        if (!deployedToken) return;
        console.debug('scanner');

        const fetchTokenValues = async () => {
            try {
                const walletAtaPubkeys = await Promise.all(
                    allWallets.map((wallet) => getAssociatedTokenAddress(new PublicKey(deployedToken), wallet.publicKey)),
                );

                const getMultipleAccountInfosChunked = async (pubKeys: Array<PublicKey>, slot: number) => {
                    const promises = [];

                    for (let i = 0; i < pubKeys.length; i += SCANNER_CHUNK_SIZE)
                        promises.push(scannerConnection.getMultipleAccountsInfo(pubKeys.slice(i, i + SCANNER_CHUNK_SIZE), { minContextSlot: slot }));

                    const result = await Promise.all(promises);

                    return result.flat();
                };

                const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
                const poolBaseTokenAccount = await getAssociatedTokenAddress(new PublicKey(WSOL.mint), poolId, true);
                const poolQuoteTokenAccount = await getAssociatedTokenAddress(new PublicKey(deployedToken), poolId, true);

                const [wsolVaultInfo, tokenVaultInfo, mintAccountInfo, ...walletAtaInfos] = await getMultipleAccountInfosChunked(
                    [poolBaseTokenAccount, poolQuoteTokenAccount, new PublicKey(deployedToken), ...walletAtaPubkeys],
                    currentSlot,
                );

                const mint = unpackMint(new PublicKey(deployedToken!), mintAccountInfo);
                const unpackAccountBalance = (info: AccountInfo<Buffer> | null | undefined) =>
                    BigInt(info ? SPL_ACCOUNT_LAYOUT.decode(info.data).amount.toString() : 0);

                const feeTokenAccounts = pumpAmmProtocolFees.map((account) =>
                    getAssociatedTokenAddress(new PublicKey(deployedToken), new PublicKey(account), true),
                );
                const feeAccounts = await getMultipleAccountInfosChunked(await Promise.all(feeTokenAccounts), currentSlot);
                const feeBalances = feeAccounts.map((info) => unpackAccountBalance(info));
                const totalFeeBalance = feeBalances.reduce((sum, balance) => sum + balance, BigInt(0));

                const tokenVaultBalance = unpackAccountBalance(tokenVaultInfo);
                const wsolVaultBalance = unpackAccountBalance(wsolVaultInfo);
                const walletBalances = walletAtaInfos.map((info) => unpackAccountBalance(info));

                const sumOfWalletBalances = walletBalances.reduce((sum, balance) => sum + balance, BigInt(0));

                const tokens = mint.supply - tokenVaultBalance - sumOfWalletBalances - totalFeeBalance;
                const tokensPercentage = (tokens * BigInt(100)) / mint.supply;

                const tokensValue = (wsolVaultBalance * tokens) / (tokenVaultBalance + tokens);

                console.log('tokensValue', tokensValue, 'wsolVaultBalance', wsolVaultBalance, 'tokenVaultBalance', tokenVaultBalance);
                setTokensPercentageUi(tokensPercentage.toString());
                setTokensValueUi(new CurrencyAmount(new Currency(WSOL.decimals), tokensValue).toFixed(2));
            } catch (error) {
                console.log(error);
            }
        };

        const fetchPoolBalance = async () => {
            try {
                const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
                const poolBaseTokenAccount = await getAssociatedTokenAddress(new PublicKey(WSOL.mint), poolId, true);
                const pow = 9;

                const accountInfo = await scannerConnection.getAccountInfo(poolBaseTokenAccount, { minContextSlot: currentSlot });
                const account = unpackAccount(poolBaseTokenAccount, accountInfo!);
                const balanceString = bigIntToStringWithDecimals(account.amount, pow);
                setPoolBalance(balanceString);

                if (removeLpAmount === '-') {
                    getLpBalance();
                }
            } catch (error) {
                console.log(error);
            }
        };
        try {
            fetchTokenValues();
            fetchPoolBalance();
        } catch (e) {
            console.log(e);
            logMessage('RPC error in scanner', 'error');
        }
    }, [deployedToken, allWallets, scannerConnection, isJitoTx, currentSlot, scanningEnabled, autoRemoveGoalSol, autoRemoveGoalSolEnabled, removeLpAmount]);

    useEffect(() => {
        if (autoActionStarted) return;
        if (!deployedToken) return;

        if (autoAction === 'poolScanner') {
            logMessage('Starting auto action for pool scanner.');
            setScanningEnabled(true);
            setScanningEnabled(true);
            setAutoActionStarted(true);
        }

        // const timeDifference = Date.now() - new Date(updatedAt).getTime();
        // if (timeDifference > 30000) return;
        // if (autoAction === 'tSell') {
        //     logMessage('Starting auto action for remove liquidity.');
        //     setScanningEnabled(true);
        //     setAutoActionStarted(true);
        // }
    }, [deployedToken, autoAction, autoActionStarted]);

    const jitoTipChange = (event: { target: { value: string } }) => {
        setJitoTip(Number(event.target.value));
    };

    const removeLpAmountChange = (event: { target: { value: string } }) => {
        setRemoveLpAmount(event.target.value);
    };

    const burnConfirmationChagne = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setBurnConfirmation(checked);
    };

    const burnLpAmountChange = (event: { target: { value: string } }) => {
        setBurnLpAmount(Number(event.target.value));
    };

    async function getLpBalance() {
        try {
            if (walletLoaded === false) {
                logMessage('WALLET IS NOT LOADED! ');
                return BigInt(0);
            }
            if (!deployedToken) {
                logMessage('Deployed token is not set!');
                return BigInt(0);
            }
            const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
            const lpMint = await getLpMint(mainConnection, new PublicKey(poolId));
            console.log('lpMint', lpMint.toBase58());
            const lpTokenAccount = getAssociatedTokenAddressSync(lpMint, wallet.publicKey, false, TOKEN_2022_PROGRAM_ID);
            console.log('lpTokenAccount', lpTokenAccount.toBase58());
            const balanceInfo = await mainConnection.getTokenAccountBalance(lpTokenAccount, 'processed');
            const walletLpBalance = BigInt(balanceInfo?.value?.amount ?? '0');

            logMessage('Your LP balance: ' + walletLpBalance + ' ');
            //const decreasedWalletLpBalance = BigInt(Math.floor((Number(walletLpBalance) / 1000) * 999));

            setBurnLpAmount(Number(walletLpBalance));
            setRemoveLpAmount(walletLpBalance.toString());
            return walletLpBalance;
        } catch (error) {
            //logMessage(`Failed to get LP balance: ${error}`, 'error');
            return BigInt(0);
        }
    }

    async function removeLiquidity(removeLpAmountValue: string, burnAcc: boolean = false) {
        try {
            logMessage(`Removing ${removeLpAmountValue} LP tokens`);
            const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
            let burnIx: TransactionInstruction | undefined = undefined;
            if (burnAcc) {
                const lpMint = await getLpMint(mainConnection, new PublicKey(poolId));
                console.log('lpMint', lpMint.toBase58());
                const lpTokenAccount = getAssociatedTokenAddressSync(lpMint, wallet.publicKey, false, TOKEN_2022_PROGRAM_ID);
                console.log(lpTokenAccount);
                burnIx = createCloseAccountInstruction(lpTokenAccount, wallet.publicKey, wallet.publicKey, [], TOKEN_2022_PROGRAM_ID);
            }

            const removeLiquidityTx = await buildRemoveLiquidityTx(
                mainConnection,
                poolId.toBase58(),
                Number(removeLpAmountValue),
                wallet.publicKey.toBase58(),
                removeLiquidityPumpswapJitoTip,
                removeLiquidityPumpswapRemoveUnits,
                removeLiquidityPumpswapRemovePriorityGas,
                burnIx ? [burnIx] : [],
            );

            removeLiquidityTx.sign([wallet]);

            const bundleId = await send0SlotTransaction(removeLiquidityTx);
            logMessage(`Bundle sent: ${bundleId}`);
        } catch (error) {
            logMessage(`Failed to remove liquidity ${error}`);
        }
    }

    async function burnLpToken(burnLpAmount: number) {
        const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
        const lpMint = await getLpMint(mainConnection, new PublicKey(poolId));
        console.log('lpMint', lpMint.toBase58());
        if (burnLpAmount === 0) {
            logMessage('Cant burn 0');
            return;
        }
        logMessage(`Burning ${burnLpAmount.toString()} LP tokens `);
        try {
            const tokenAccount = getAssociatedTokenAddressSync(lpMint, wallet.publicKey, false, TOKEN_2022_PROGRAM_ID);
            console.log('tokenAccount', tokenAccount.toBase58());
            const signature = await burnToken(
                mainConnection,
                wallet,
                wallet,
                tokenAccount,
                burnLpAmount,
                removeLiquidityPumpswapBurnLpPriorityGas,
                TOKEN_2022_PROGRAM_ID,
            );
            logMessage(`TX sent: ${signature}`);
        } catch (error) {
            logMessage(`Failed to burn LP tokens ${error}`);
        }
    }

    return (
        <section>
            <div className='header-line'></div>
            <section className='liq-sum-tok'>
                <div className='liq-text'>
                    <div className='liq'></div>
                    <h1>{poolBalance}</h1>
                </div>
                <div className='sum-text'>
                    <div className='sum'></div>
                    <h1> {(Number(poolBalance) + totalWalletsBalance / LAMPORTS_PER_SOL).toFixed(3)}</h1>
                </div>
                <div className='tok-text'>
                    <div className='tok'></div>
                    <div className='text'>
                        <div className='text-tok'>Tokens: {tokensPercentageUi}%</div>
                        <div className='text-sol'>SOL {tokensValueUi}</div>
                    </div>

                    <section className='list'>
                        <div className='sel-list-text'>
                            <select
                                value={connections.findIndex((connection) => connection.rpcEndpoint == scannerConnection.rpcEndpoint)}
                                onChange={(event) => setScannerConnection(connections[Number(event.target.value)])}
                            >
                                {connections.map((connection, index) => (
                                    <option value={index} key={index}>
                                        {new URL(connection.rpcEndpoint).host}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </section>
                </div>
            </section>
            <section className='scan-deploy-btn'>
                <div className='scan-btn'>
                    <button className='scan' onClick={() => setScanningEnabled(true)}>
                        <div className='icon-scan'></div>Scan Pool Balance
                    </button>
                </div>
                <div className='jito'>
                    Tip
                    <input type='value' className='jito-tip' value={jitoTip} onChange={jitoTipChange} />
                </div>
            </section>
            <section className='scan-deploy-btn'>
                auto remove goal
                <input
                    value={autoRemoveGoalSol}
                    type={'number'}
                    onChange={(e) => {
                        setAutoRemoveGoalSol(Number(e.target.value));
                    }}
                ></input>
                <input
                    type={'checkbox'}
                    checked={autoRemoveGoalSolEnabled}
                    onClick={() => {
                        setAutoRemoveGoalSolEnabled((prev) => !prev);
                    }}
                ></input>
            </section>
            <section className='remove-get-btn'>
                <div className='remove-lp-buttons'>
                    <button className='button-150' onClick={() => removeLiquidity(Math.floor((Number(removeLpAmount) * 999) / 1000).toString())}>
                        Remove 99.9%
                    </button>
                    <button className='button-150' onClick={() => removeLiquidity(removeLpAmount, true)}>
                        Remove 100%
                    </button>
                    0Slot REMOVE RPC
                    <input
                        type={'checkbox'}
                        checked={isJitoTx}
                        onClick={() => {
                            setIsJitoTx((prev) => !prev);
                        }}
                    ></input>
                    <button className='button-accent' onClick={() => getLpBalance()}>
                        Get Wallet LP Balance
                    </button>
                </div>
            </section>
            <section className='deploy-lp-input'>
                <input placeholder='lpBalance' className='lp-balance-input' onChange={removeLpAmountChange} value={removeLpAmount}></input>
                lpBalance
            </section>
            <div className='header-line'></div>
            <section className='burn-section'>
                <section className='burn-container'>
                    <div className='must-check'>
                        <input type='checkbox' checked={burnConfirmation} onChange={burnConfirmationChagne} className='must-check-to-burn' />
                        MUST CHECK TO BURN
                    </div>
                </section>
                <section className='burn-btns'>
                    <div className='lpBalance-input-burn'>
                        <input onChange={burnLpAmountChange} value={burnLpAmount} placeholder='lpBalance' className='lpbalance-burn'></input>
                    </div>
                    <div className='btns'>
                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpToken(burnLpAmount);
                            }}
                        >
                            BURN LP
                        </button>
                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpToken(Math.floor(burnLpAmount / 1000));
                            }}
                        >
                            Burn 0.1%
                        </button>

                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpToken(Math.floor(burnLpAmount / 5000));
                            }}
                        >
                            Burn 0.02%
                        </button>
                    </div>
                </section>
            </section>
        </section>
    );
}
